import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ScanLine, CheckCircle2, XCircle, AlertTriangle, Camera, ShieldAlert, Loader2 } from "lucide-react";
import { validateTicket } from "@/lib/tickets.functions";
import { listMyValidatableEvents } from "@/lib/staff.functions";
import { QrScanner } from "@/components/QrScanner";
import { useAuth } from "@/hooks/useAuth";
import { getAuthHeaders } from "@/lib/auth-helper";

export const Route = createFileRoute("/validate")({
  component: ValidateTicketPage,
  head: () => ({
    meta: [
      { title: "Validar Ticket — ZYNTRA" },
      { name: "description", content: "Validá entradas en la puerta del evento." },
    ],
  }),
});

type ValidationResult = {
  status: "valid" | "used" | "invalid";
  message: string;
  ticketId?: string;
  eventTitle?: string;
  seatLabel?: string;
  inventoryType?: string;
} | null;

type ValidatableEvent = { id: string; title: string; date: string; role: string };

function ValidateTicketPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [result, setResult] = useState<ValidationResult>(null);
  const [loading, setLoading] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [accessChecking, setAccessChecking] = useState(true);
  const [events, setEvents] = useState<ValidatableEvent[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeEventId, setActiveEventId] = useState<string>("");

  useEffect(() => {
    if (!authLoading && !user) {
      navigate({ to: "/login", search: { redirect: "/validate" } as any });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setAccessChecking(true);
      try {
        const headers = await getAuthHeaders();
        const res = await listMyValidatableEvents({ headers } as any);
        if (cancelled) return;
        setEvents(res.events as ValidatableEvent[]);
        setIsAdmin(!!res.isAdmin);
        if (res.events.length === 1) setActiveEventId(res.events[0].id);
      } finally {
        if (!cancelled) setAccessChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  async function runValidation(qr: string) {
    if (!qr.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const headers = await getAuthHeaders();
      const res = await validateTicket({ data: { qrData: qr.trim() }, headers } as any);
      // If a specific event is selected and the ticket is from another event, surface a warning
      if (
        activeEventId &&
        (res as any).eventTitle &&
        events.find((e) => e.id === activeEventId)?.title !== (res as any).eventTitle
      ) {
        setResult({
          status: "invalid",
          message: `Este ticket pertenece a "${(res as any).eventTitle}", no al evento seleccionado.`,
          ticketId: (res as any).ticketId,
          eventTitle: (res as any).eventTitle,
        });
      } else {
        setResult(res as ValidationResult);
      }
    } catch (err: any) {
      setResult({ status: "invalid", message: err.message || "Error al validar" });
    } finally {
      setLoading(false);
    }
  }

  function handleScanned(decoded: string) {
    setScannerOpen(false);
    setCode(decoded);
    runValidation(decoded);
  }

  if (authLoading || (user && accessChecking)) {
    return (
      <div className="mx-auto flex max-w-md items-center justify-center px-4 py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) return null;

  const hasAccess = isAdmin || events.length > 0;

  if (!hasAccess) {
    return (
      <div className="mx-auto max-w-md px-4 pb-20 pt-10 sm:px-6">
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-6 text-center">
          <ShieldAlert className="mx-auto mb-3 h-10 w-10 text-destructive" />
          <h1 className="text-lg font-bold text-foreground">Acceso restringido</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Esta sección es para organizadores, admins y staff asignado a un evento.
          </p>
          <Link
            to="/"
            className="mt-5 inline-block rounded-xl bg-secondary px-4 py-2 text-sm font-bold text-secondary-foreground hover:bg-accent"
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  const activeEvent = events.find((e) => e.id === activeEventId);

  return (
    <div className="mx-auto max-w-md px-4 pb-20 pt-10 sm:px-6">
      <div className="text-center mb-6">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl gradient-primary">
          <ScanLine className="h-7 w-7 text-primary-foreground" />
        </div>
        <h1 className="text-2xl font-black text-foreground">Validar Ticket</h1>
        <p className="mt-1 text-sm text-muted-foreground">Escaneá el QR con la cámara o ingresalo a mano</p>
      </div>

      {/* Event selector */}
      {events.length > 1 && (
        <div className="mb-4 rounded-2xl border border-border bg-card p-4">
          <label className="text-xs font-medium text-muted-foreground">Evento activo</label>
          <select
            value={activeEventId}
            onChange={(e) => setActiveEventId(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-border bg-input px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Todos los eventos a los que tengo acceso</option>
            {events.map((e) => (
              <option key={e.id} value={e.id}>
                {e.title} — {e.role}
              </option>
            ))}
          </select>
        </div>
      )}
      {activeEvent && (
        <div className="mb-4 rounded-xl border border-primary/30 bg-primary/10 px-3 py-2 text-xs">
          <span className="text-muted-foreground">Validando para:</span>{" "}
          <strong className="text-primary">{activeEvent.title}</strong>{" "}
          <span className="text-muted-foreground">({activeEvent.role})</span>
        </div>
      )}

      <button
        onClick={() => setScannerOpen(true)}
        className="mb-4 flex w-full items-center justify-center gap-2 rounded-2xl gradient-primary py-4 text-sm font-bold text-primary-foreground glow-primary transition-all hover:opacity-90"
      >
        <Camera className="h-5 w-5" />
        Escanear con cámara
      </button>

      <div className="rounded-2xl border border-border bg-card p-5">
        <label className="text-xs font-medium text-muted-foreground">O ingresá el código manualmente</label>
        <input
          type="text"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            setResult(null);
          }}
          placeholder="ZYNTRA-xxxx..."
          className="mt-1.5 w-full rounded-lg border border-border bg-input px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring font-mono"
        />
        <button
          onClick={() => runValidation(code)}
          disabled={loading || !code.trim()}
          className="mt-3 w-full rounded-xl border border-border bg-secondary py-3 text-sm font-bold text-secondary-foreground transition-all hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {loading ? "Validando..." : "Validar manualmente"}
        </button>
      </div>

      {scannerOpen && <QrScanner onScan={handleScanned} onClose={() => setScannerOpen(false)} />}

      {result && (
        <div
          className={`mt-5 rounded-2xl border p-5 transition-all ${
            result.status === "valid"
              ? "border-success/30 bg-success/10"
              : result.status === "used"
              ? "border-warning/30 bg-warning/10"
              : "border-destructive/30 bg-destructive/10"
          }`}
        >
          <div className="flex items-start gap-3">
            {result.status === "valid" && <CheckCircle2 className="h-6 w-6 text-success shrink-0 mt-0.5" />}
            {result.status === "used" && <AlertTriangle className="h-6 w-6 text-warning shrink-0 mt-0.5" />}
            {result.status === "invalid" && <XCircle className="h-6 w-6 text-destructive shrink-0 mt-0.5" />}
            <div>
              <h3
                className={`text-sm font-bold ${
                  result.status === "valid"
                    ? "text-success"
                    : result.status === "used"
                    ? "text-warning"
                    : "text-destructive"
                }`}
              >
                {result.status === "valid" ? "✅ Válido" : result.status === "used" ? "⚠️ Ya utilizado" : "❌ Inválido"}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">{result.message}</p>
              {result.eventTitle && (
                <p className="text-xs text-muted-foreground mt-2">
                  Evento: <strong className="text-foreground">{result.eventTitle}</strong>
                </p>
              )}
              {result.seatLabel && (
                <p className="mt-2 inline-block rounded-lg border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs">
                  <span className="text-muted-foreground">{result.inventoryType === "seated" ? "Asiento: " : "Zona: "}</span>
                  <strong className="text-primary">{result.seatLabel}</strong>
                </p>
              )}
              {result.ticketId && (
                <p className="text-xs text-muted-foreground font-mono mt-2">ID: {result.ticketId.slice(0, 8)}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
